import { ChevronRight } from 'lucide-react'
import type { JsonValue } from './json-tree'

const isPlainObject = (value: JsonValue): value is { [key: string]: JsonValue } =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isPrimitive = (value: JsonValue): boolean =>
  value === null || typeof value !== 'object'

const formatPrimitive = (value: JsonValue): string => {
  if (value === null) return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

/** An object whose values are all primitives renders nicely as a key/value table. */
const isFlatObject = (value: JsonValue): boolean =>
  isPlainObject(value) && Object.values(value).every(isPrimitive)

interface JsonContentProps {
  path: string[]
  value: JsonValue
}

export const JsonContent: React.FC<JsonContentProps> = ({ path, value }) => {
  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-1 border-b border-border pb-3 text-sm text-muted-foreground">
        {path.length === 0 ? (
          <span className="font-medium text-foreground">Root</span>
        ) : (
          path.map((segment, index) => (
            <span key={index} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="size-3.5 opacity-50" />}
              <span
                className={
                  index === path.length - 1
                    ? 'font-medium text-foreground'
                    : undefined
                }
              >
                {segment}
              </span>
            </span>
          ))
        )}
      </div>

      <div className="mt-4 min-h-0 flex-1 overflow-auto">
        {isPrimitive(value) ? (
          <div className="rounded-md bg-muted px-4 py-3 font-mono text-sm break-words">
            {formatPrimitive(value)}
          </div>
        ) : isFlatObject(value) ? (
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(value as Record<string, JsonValue>).map(
                ([key, val]) => (
                  <tr key={key} className="border-b border-border/60">
                    <td className="py-2 pr-4 align-top font-medium whitespace-nowrap text-muted-foreground">
                      {key}
                    </td>
                    <td className="py-2 font-mono break-words">
                      {formatPrimitive(val)}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        ) : (
          <pre className="rounded-md bg-muted p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
            {JSON.stringify(value, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}
